import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Badge, FilterSelect } from "@td/ui";
import { translate } from "@td/shared_utils";
import { debounce, uniqBy } from "lodash";
import { useAdminsSearch } from "../hooks";
import { useCurrentUser } from "../contexts/current-user-context";
import { useSuggestions } from '../contexts/suggestions-context';
import { unassignedOption } from "../constants";
import { mapAssigneesSuggestionsToOptions } from '../utils/suggestions';

const LOAD_OPTIONS_LIMIT = 8;
const LOAD_OPTIONS_DEBOUNCE_DELAY = 500;

const filterDropdownProps = {
  style: {
    minWidth: 172,
  },
  "data-testid": "assignee-filter",
};

const AssignedToFilter = React.memo(({ value, onChange }) => {
  const currentUser = useCurrentUser();
  const { assignees } = useSuggestions();
  const { loadAdmins } = useAdminsSearch();

  const suggestedAssigneesOptions = useMemo(() => {
    return mapAssigneesSuggestionsToOptions(assignees);
  }, [assignees]);

  const defaultOptions = useMemo(() => {
    return uniqBy([
      {
        value: currentUser.id,
        label: `${translate(null, "care_team_tasks.filters.fields.assigned_to", "me")} (${currentUser.name})`,
      },
      unassignedOption,
      ...suggestedAssigneesOptions,
      ...value,
    ], "value");
  }, [value]);

  const loadOptions = async (inputValue, callback) => {
    const admins = await loadAdmins({
      nameQueryStr: inputValue,
      emailQueryStr: inputValue,
    }, LOAD_OPTIONS_LIMIT);

    callback(admins.map(({ id, name }) => ({ value: parseInt(id), label: name })));
  };

  const debouncedLoadOptions = debounce((inputValue, callback) => {
    loadOptions(inputValue, callback);
  }, LOAD_OPTIONS_DEBOUNCE_DELAY);

  const renderLabel = useCallback(({ value, onClick }) => (
    <React.Fragment>
      {translate(null, "care_team_tasks.filters.fields.assigned_to", "label")}

      {(value.length === 1 && value[0]["value"] === currentUser.id) ? (
        <Badge onClose={onClick}>
          {translate(null, "care_team_tasks.filters.fields.assigned_to", "me")}
        </Badge>
      ) : (
        <React.Fragment>
          {value.length > 0 && (
            <Badge onClose={onClick}>
              {value.length}
            </Badge>
          )}
        </React.Fragment>
      )}
    </React.Fragment>
  ), [currentUser]);

  return (
    <FilterSelect
      async
      isMulti
      defaultOptions={defaultOptions}
      loadOptions={debouncedLoadOptions}
      value={value}
      onChange={onChange}
      placeholder={translate(null, "care_team_tasks.filters.fields.assigned_to", "placeholder")}
      filterDropdownProps={filterDropdownProps}
      renderLabel={renderLabel}
    />
  );
});

AssignedToFilter.propTypes = {
  value: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
};

AssignedToFilter.displayName = "AssignedToFilter";

export default AssignedToFilter;
